import axios from 'axios'

const PRODUCTION_API_URL =
  'https://f8h3l9bsb0.execute-api.ap-south-1.amazonaws.com/salesorder-api'
const LOCAL_API_URL = 'http://localhost:3001'

const DEFAULT_API_URL =
  process.env.NODE_ENV === 'development' ? LOCAL_API_URL : PRODUCTION_API_URL

function normalizeUrl(url) {
  return (url || '').trim().replace(/\/+$/, '')
}

/**
 * Resolves API base URL.
 * - Local (`npm start`): REACT_APP_API_URL from .env.development → http://localhost:3001
 * - Production build: REACT_APP_API_URL from .env.production → AWS salesorder-api
 * Falls back when .env is missing or points at the React app (localhost:3000).
 */
export function getResolvedApiUrl() {
  const fromEnv = normalizeUrl(process.env.REACT_APP_API_URL)
  if (!fromEnv || /localhost:3000/i.test(fromEnv)) {
    return normalizeUrl(DEFAULT_API_URL)
  }
  // While developing, never keep the main SCM production URL if env was mis-set
  if (
    process.env.NODE_ENV === 'development' &&
    /f8h3l9bsb0\.execute-api|funs6nk6j9\.execute-api/i.test(fromEnv)
  ) {
    return normalizeUrl(LOCAL_API_URL)
  }
  return fromEnv
}

export const API_URL = getResolvedApiUrl()

function shouldRewriteRequestUrl(url) {
  if (!url || typeof url !== 'string') return true
  const trimmed = url.trim()
  if (!trimmed) return true
  if (/localhost:3000/i.test(trimmed)) return true
  // Local dev: rewrite hard-coded SCM API hosts to the local API
  if (
    process.env.NODE_ENV === 'development' &&
    (/f8h3l9bsb0\.execute-api/i.test(trimmed) ||
      /funs6nk6j9\.execute-api/i.test(trimmed))
  ) {
    return true
  }
  if (!/^https?:\/\//i.test(trimmed)) return true
  return false
}

function getCurrentUserId() {
  try {
    const raw = localStorage.getItem('user')
    if (raw) {
      const currentUser = JSON.parse(raw)
      const id = currentUser?.id
      if (id) return String(id)
    }
  } catch {
    // ignore parse errors
  }
  const fallback = localStorage.getItem('id')
  return fallback ? String(fallback) : null
}

function isBinaryUploadBody(data) {
  if (data == null) return false
  if (typeof Blob !== 'undefined' && data instanceof Blob) return true
  if (typeof File !== 'undefined' && data instanceof File) return true
  if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) return true
  if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView?.(data)) return true
  return false
}

function attachCurrentUserId(data, userId) {
  if (!userId || data == null) return data

  // Never spread File/Blob — that replaces the file with ~44B JSON { currentUserId }.
  if (isBinaryUploadBody(data) || data instanceof FormData) {
    return data
  }

  if (Array.isArray(data)) {
    return data
  }

  if (typeof data === 'object') {
    if (data.currentUserId == null || data.currentUserId === '') {
      return { ...data, currentUserId: userId }
    }
    return data
  }

  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data)
      if (Array.isArray(parsed)) {
        return data
      }
      if (
        parsed &&
        typeof parsed === 'object' &&
        (parsed.currentUserId == null || parsed.currentUserId === '')
      ) {
        return JSON.stringify({ ...parsed, currentUserId: userId })
      }
    } catch {
      // leave string body as-is
    }
  }

  return data
}

let interceptorRegistered = false

export function registerApiInterceptor() {
  if (interceptorRegistered) return
  interceptorRegistered = true

  const resolved = API_URL

  if (process.env.NODE_ENV === 'development') {
    console.info(`[API] Using ${resolved}`)
    const fromEnv = process.env.REACT_APP_API_URL
    if (!fromEnv || /localhost:3000/i.test(fromEnv)) {
      console.warn(
        '[API] REACT_APP_API_URL was missing or pointed at localhost:3000. ' +
          `Using ${resolved}. Update .env.development and restart "npm start" to apply.`
      )
    }
  }

  axios.interceptors.request.use((config) => {
    if (shouldRewriteRequestUrl(config.url)) {
      config.url = resolved
    }

    const method = String(config.method || 'get').toLowerCase()
    if (method === 'post' || method === 'put' || method === 'patch') {
      const userId = getCurrentUserId()
      if (userId) {
        config.data = attachCurrentUserId(config.data, userId)
      }
    }

    return config
  })
}
